import App from "+/Engine/App";
import Entity from "+/Engine/Entity";
import EntityReference from "+/Engine/EntityReference";
import DataTypes from "+/Engine/Helpers/DataTypes";
import Transformer from "+/Engine/Transformers/Transformer";
import "+/Engine/Transformers/Add";
import "+/Engine/Transformers/JsonEncode";
import "+/Engine/Transformers/Not";
import "+/Engine/Transformers/Subtract";
import "+/Engine/Transformers/Uuidv4";

// Source discriminators recognised by `resolveOneParam`'s switch. A
// resolved value (e.g. an Element-shaped entity payload that happens to
// carry a `source: "destination"` domain field) is passed through as
// bare data when its `source` isn't one of these.
const KNOWN_SOURCE_DISCRIMINATORS = new Set([
  "constant",
  "object",
  "nested",
  "params",
  "context",
  "contextField",
  "currentEntity",
  "currentEntityField",
  "currentUser",
]);

/**
 * Resolves a paramsDef map into a concrete params object — the one place that knows how
 * a source definition becomes a value. Used by:
 *
 *   - Routine.js   — routine params; context is the event's params map
 *   - Foreach.js   — sub-event params; context is event params + contexts, entity is the item
 *   - Entity.js    — entity-action → event params; context is caller contexts map, entity is self
 *
 * All supported source types (a paramsDef entry is either a source def with `.source`,
 * a transform-only shorthand with just `.transform`, or a bare already-resolved value):
 *
 *   - constant         → sourceDef.value
 *   - object           → sourceDef.object
 *   - nested           → resolveActionParams(sourceDef.nesting, …)
 *   - params           → context[sourceDef.paramName]        (routine style)
 *   - context          → context[sourceDef.context]          (action style, same semantics)
 *   - contextField     → context[sourceDef.context][sourceDef.fieldName]
 *   - currentEntity    → entity.getPk()                       (deferred if entity is null)
 *   - currentEntityField → entity[sourceDef.fieldName]        (deferred if entity is null)
 *   - currentUser      → App.getCurrent().getCurrentUserEntityPk() (app-scoped user entity
 *                        PK pulled from the active session — NOT from context or entity)
 *
 * Post-resolution modifiers (applied in this order to the raw value):
 *
 *   - path        — dot-traversal of the value
 *   - default     — fallback when value is null/undefined after path
 *   - transform   — runs the value through the Transformer registry
 *   - operator    — wraps the final value as `{ value, operator }` for whereCriteria consumers
 *
 * Transform-only fields (no source, just `.transform`) represent self-field mutation for
 * UpdateEntities and are returned as `{ isTransformField: true, transform }` — routines
 * that consume them know what to do.
 */
export default function resolveActionParams(paramsDef, context, entity = null) {
  DataTypes.assertObject(paramsDef);
  DataTypes.assertObject(context);

  try {
    const resolved = {};
    Object.entries(paramsDef).forEach(([paramName, sourceDef]) => {
      resolved[paramName] = resolveOneParam(sourceDef, context, entity);
    });
    return resolved;
  } catch (e) {
    throw new Error(`resolveActionParams failed: ${e.message}`, { cause: e });
  }
}

/**
 * Resolves a single paramsDef entry. Exported so callers (e.g. Routine.js) can resolve
 * one entry at a time without building a wrapping paramsDef object.
 */
export function resolveOneParam(sourceDef, context, entity = null) {
  // A bare non-object value (string, number, boolean, null, undefined, array) is pre-resolved
  // — pass through as-is. This lets ForEach feed a mix of pre-resolved values and source defs
  // through the same helper without a normalization pass.
  if (sourceDef === null || sourceDef === undefined || typeof sourceDef !== "object" || Array.isArray(sourceDef)) {
    return sourceDef;
  }

  // Transform-only field: no source, just a transform. UpdateEntities turns this into a
  // self-field SQL mutation (e.g. `indexInParent = indexInParent + 1`).
  if (!sourceDef.source) {
    if (sourceDef.transform) {
      return { isTransformField: true, transform: sourceDef.transform };
    }
    return sourceDef;
  }

  // Only treat the object as a source-def when its `source` value is one of
  // the schema-defined source discriminators. Pre-resolved entity payloads
  // can legitimately carry a `source` field with a domain value (e.g.
  // Element rows have `source: "theme" | "destination"`) — the previous
  // pass-through check missed those because it only gated on `!source`,
  // and the unknown discriminator then fell through to the switch's
  // `default` clause and threw "unsupported source". Concretely this
  // broke WrapElements: the inner `createElement` routine returns an
  // Element-shaped object whose `source: "destination"` collided with
  // the resolver schema, so the `moveIntoWrapper` ForEach never moved
  // any children into the new wrapper.
  if (!KNOWN_SOURCE_DISCRIMINATORS.has(sourceDef.source)) {
    return sourceDef;
  }

  let value;
  switch (sourceDef.source) {
    case "constant":
      DataTypes.assertObjectKeyExists("value", sourceDef);
      value = sourceDef.value;
      break;
    case "object":
      DataTypes.assertObjectKeyExists("object", sourceDef);
      value = sourceDef.object;
      break;
    case "nested":
      DataTypes.assertObjectKeyExistsOfType("nesting", sourceDef, DataTypes.assertObject);
      value = resolveActionParams(sourceDef.nesting, context, entity);
      break;
    case "params":
      DataTypes.assertObjectKeyExistsOfType("paramName", sourceDef, DataTypes.assertString);
      assertResolvableOrOptional(
        Object.prototype.hasOwnProperty.call(context, sourceDef.paramName),
        sourceDef,
        `context entry "${sourceDef.paramName}" (source: params)`,
      );
      value = context[sourceDef.paramName];
      break;
    case "context":
      DataTypes.assertObjectKeyExistsOfType("context", sourceDef, DataTypes.assertString);
      assertResolvableOrOptional(
        Object.prototype.hasOwnProperty.call(context, sourceDef.context),
        sourceDef,
        `context entry "${sourceDef.context}" (source: context)`,
      );
      value = context[sourceDef.context];
      break;
    case "contextField": {
      DataTypes.assertObjectKeyExistsOfType("context", sourceDef, DataTypes.assertString);
      DataTypes.assertObjectKeyExistsOfType("fieldName", sourceDef, DataTypes.assertString);
      const ctxObj = context[sourceDef.context];
      assertResolvableOrOptional(
        ctxObj != null && typeof ctxObj === "object" && Object.prototype.hasOwnProperty.call(ctxObj, sourceDef.fieldName),
        sourceDef,
        `context["${sourceDef.context}"]["${sourceDef.fieldName}"] (source: contextField)`,
      );
      value = ctxObj != null && typeof ctxObj === "object" ? ctxObj[sourceDef.fieldName] : undefined;
      break;
    }
    case "currentEntity":
      // When no entity is in scope (e.g. Routine.js resolving a Foreach routine's paramsDef
      // before per-item iteration), defer: return the source def unchanged so the downstream
      // caller (with an entity in hand) can resolve it.
      if (!(entity instanceof Entity)) {
        return sourceDef;
      }
      // Return the FULL field data map (not just getPk()) so sub-event routines that use
      // path: "parentElement" / "indexInParent" on the `element` param can traverse to real
      // values. Matches PHP ResolveActionParams::resolve, which returns the full entity data
      // for currentEntity. PK-only would break path traversal and silently no-op routines.
      value = entity.getBasicFieldData();
      break;
    case "currentEntityField":
      if (!(entity instanceof Entity)) {
        return sourceDef;
      }
      DataTypes.assertObjectKeyExistsOfType("fieldName", sourceDef, DataTypes.assertString);
      assertResolvableOrOptional(
        sourceDef.fieldName in entity,
        sourceDef,
        `currentEntity field "${sourceDef.fieldName}"`,
      );
      value = entity[sourceDef.fieldName];
      if (value instanceof EntityReference) {
        value = value.pk;
      }
      break;
    case "currentUser":
      // Session-scoped lookup: the active app's current user entity PK, resolved from
      // `window.blclient.data.user.userId` plus the app's `currentUser` config. Never reads
      // from `context` or `entity` — routines are deliberately not handed any contexts map.
      value = App.getCurrent().getCurrentUserEntityPk();
      break;
    default:
      throw new Error(`unsupported source "${sourceDef.source}"`);
  }

  // Path: dot-separated traversal of the resolved value.
  if (sourceDef.path) {
    for (const key of sourceDef.path.split(".")) {
      if (value == null) {
        break;
      }
      value = value[key];
    }
  }

  // Default: fallback when the resolved-then-traversed value is null/undefined.
  if ((value === undefined || value === null) && sourceDef.default !== undefined) {
    value = sourceDef.default;
  }

  // Transform: run through the Transformer registry. Accepts direct-key, wrapped-params,
  // or shorthand-string shapes — see Transformers/README.md.
  if (sourceDef.transform) {
    value = applyTransform(value, sourceDef.transform, context, entity);
  }

  // Operator: wrap for whereCriteria consumers that need (operator, value) together.
  if (sourceDef.operator) {
    return { value, operator: sourceDef.operator };
  }

  return value;
}

/**
 * Fails loudly when a lookup can't be satisfied, unless the paramDef marks itself as
 * tolerating absence via `default` (a fallback value is declared) or `optional: true`
 * (no fallback, but the caller knows the value may be missing). Without this, missing
 * keys silently become undefined and propagate into routines that expected a real value.
 * Mirrors `assertResolvableOrOptional` in the PHP helper.
 */
function assertResolvableOrOptional(isPresent, sourceDef, what) {
  if (isPresent) {
    return;
  }
  if (sourceDef.optional === true || Object.prototype.hasOwnProperty.call(sourceDef, "default")) {
    return;
  }
  throw new Error(
    `Required paramDef lookup failed: ${what} is not present in the resolution scope. `
    + "Declare 'default' (value to substitute) or 'optional: true' on the paramDef if absence is expected.",
  );
}

function applyTransform(value, transformDef, context, entity) {
  const normalized = typeof transformDef === "string"
    ? { type: transformDef, params: {} }
    : transformDef;

  DataTypes.assertObject(normalized);
  DataTypes.assertObjectKeyExistsOfType("type", normalized, DataTypes.assertString);

  const rawParams = normalized.params && typeof normalized.params === "object"
    ? normalized.params
    : Object.fromEntries(Object.entries(normalized).filter(([k]) => k !== "type"));

  const resolvedParams = resolveActionParams(rawParams, context, entity);
  return Transformer.get(normalized.type).transform(value, resolvedParams);
}
